import { translate } from 'i18n/i18n'
import { isNil } from 'lodash'
import { userDetailsStore } from 'stores/userDetailsStore/userDetailsStore'
import { MM_PER_INCH } from 'utils/unitConverter/conversionFactors'
import type { TUnitConverterOptionsWithUnits, TUnitConverterTypicalReturn } from 'utils/unitConverter/types'

type TEvapotranspirationUnit = 'mm' | 'in'

export const evapotranspiration: Record<
  'evapotranspiration',
  (
    evapotranspirationAmount?: number | null,
    options?: TUnitConverterOptionsWithUnits<TEvapotranspirationUnit>,
  ) => TUnitConverterTypicalReturn
> = {
  evapotranspiration: (
    evapotranspirationAmount: number | null = null,
    {
      decimalPlaces,
      inputUnit = 'mm',
      outputUnit = userDetailsStore.getState().rain === 'IMPERIAL' ? 'in' : 'mm',
    }: {
      decimalPlaces?: number
      inputUnit?: TEvapotranspirationUnit
      outputUnit?: TEvapotranspirationUnit
    } = {},
  ) => {
    const defaultNumberOfDecimalPlaces = outputUnit === 'in' ? 3 : 2
    const shouldConvertFromMMToIN = inputUnit === 'mm' && outputUnit === 'in'
    const shouldConvertFromINToMM = inputUnit === 'in' && outputUnit === 'mm'

    let convertedValue: number | null

    if (isNil(evapotranspirationAmount)) {
      convertedValue = null
    } else if (shouldConvertFromMMToIN) {
      convertedValue = evapotranspirationAmount / MM_PER_INCH
    } else if (shouldConvertFromINToMM) {
      convertedValue = evapotranspirationAmount * MM_PER_INCH
    } else {
      convertedValue = Number(evapotranspirationAmount)
    }

    const decimalPlacesToUse = decimalPlaces ?? defaultNumberOfDecimalPlaces

    const unitSymbol =
      outputUnit === 'mm' ? translate.measurements.millimeters.unit() : translate.measurements.inches.unit()

    const label = translate.phrases.banyanApp('Evapotranspiration')

    return {
      categoryTitle: () => translate.phrases.templates('{{label}} ({{unitSymbol}})', { label, unitSymbol }),
      categoryTitleWithoutUnit: () => label,
      defaultNumberOfDecimalPlaces: () => defaultNumberOfDecimalPlaces,
      suffix: () => unitSymbol,
      shortTitle: () => label,
      title: () => translate.phrases.templates('{{label}} ({{unitSymbol}})', { label, unitSymbol }),
      titleWithoutUnit: () => label,
      value: () => {
        if (isNil(convertedValue)) return null

        return +convertedValue?.toFixed(decimalPlacesToUse)
      },
      valueAsString: () => {
        return outputUnit === 'mm'
          ? translate.measurements.millimeters.value(convertedValue, decimalPlacesToUse)
          : translate.measurements.inches.value(convertedValue, decimalPlacesToUse)
      },
      valueWithSuffix: () => {
        return outputUnit === 'mm'
          ? translate.measurements.millimeters.valueWithUnit(convertedValue, decimalPlacesToUse)
          : translate.measurements.inches.valueWithUnit(convertedValue, decimalPlacesToUse)
      },
      valueWithNoRounding: () => convertedValue,
    }
  },
}
